/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/accountDetails.scss';

import React, {FocusEventHandler, MouseEventHandler, useContext} from 'react';
import Account from "../../../models/accountModel";
import {AwesomeIcon, ChangeFunction, ErrorType, Loader} from '@dvrd/dvr-controls';
import {AccountAdminErrors, Country, DataMode} from "../../../util/interfaces";
import {BolmateButton, BolmateInput} from "../../controls";
import CountryField from "../../controls/country/field/countryField";
import {AppContext} from "../../misc/appContext";
import BolmateSwitch from "../../controls/switch/bolmateSwitch";

interface Props {
    onChange: (key: string, entity: 'account' | 'settings') => ChangeFunction;
    onSubmit: MouseEventHandler;
    onClose: MouseEventHandler;
    onClickDelete: MouseEventHandler;
    onResetError: (key: keyof AccountAdminErrors) => FocusEventHandler;
    onChangeMode: (mode: DataMode) => MouseEventHandler;
    mode: DataMode;
    errors: AccountAdminErrors;
    account: Account;
    loading: boolean;
}

export default function AccountDetails(props: Props) {
    const context = useContext(AppContext);
    const {
        account, errors, onChangeMode, mode, loading, onChange, onResetError, onClickDelete, onClose, onSubmit
    } = props;

    function userIsAdmin(): boolean {
        const {user} = context.userContext;
        return (user && user.isAdmin()) === true;
    }

    function renderHeader() {
        return (
            <div className='header-container'>
                <div className='header-items-container'>
                    <BolmateButton onClick={onClose} label='Terug' disabled={loading}
                                   ornaments={{element: <AwesomeIcon name='chevron-left'/>}}/>
                    <label className='header-title'>Account details</label>
                </div>
                {userIsAdmin() ?
                    <BolmateButton onClick={onClickDelete} label='Verwijderen' color={'red'} secondary disabled={loading}
                                   ornaments={{element: <AwesomeIcon name='trash-alt'/>}}/> : <div/>}
            </div>
        )
    }

    function renderBody() {
        const {
            settings, companyName, telNumber, cocNumber, vatNumber, address, zipCode, city, country, iban,
            doesAutoSend, email
        } = account;
        return (
            <div className='body-container'>
                <Loader active={loading}/>
                {userIsAdmin() && (mode === DataMode.DETAILS ?
                    <BolmateButton onClick={onChangeMode(DataMode.EDIT)} label='Wijzigen' className='mode-btn'/> :
                    <BolmateButton onClick={onChangeMode(DataMode.DETAILS)} label='Annuleren' className='mode-btn'/>)}
                <div className='fields-container'>
                    <label className='fields-title'>Account</label>
                    <div className='fields'>
                        <div className='left'>
                            {renderField('company_name', 'Bedrijfsnaam', companyName, 'account',
                                errors.companyName,
                                'companyName')}
                            {renderField('tel_number', 'Telefoonnummer', telNumber, 'account', errors.telNumber,
                                'telNumber')}
                            {renderField('vat_number', 'BTW-nummer', vatNumber, 'account')}
                            {renderField('zip_code', 'Postcode', zipCode, 'account', errors.zipCode, 'zipCode')}
                            {renderCountryField(country)}
                        </div>
                        <div className='right'>
                            {renderField('email', 'E-mailadres', email, 'account', errors.email, 'email')}
                            {renderField('coc_number', 'KvK-nummer', cocNumber, 'account', errors.coc, 'coc')}
                            {renderField('address', 'Adres', address, 'account', errors.address, 'address')}
                            {renderField('city', 'Stad', city, 'account', errors.city, 'city')}
                            {renderField('iban', 'IBAN', iban, 'account')}
                        </div>
                    </div>
                </div>
                <div className='fields-container'>
                    <label className='fields-title'>Instellingen</label>
                    <div className='fields'>
                        <div className='left'>
                            {renderField('invoice_prefix', 'Factuurnummer', settings.invoicePrefix, 'settings')}
                            {renderField('invoice_start', 'Factuurnummer start', settings.invoiceStart, 'settings')}
                            {renderToggleField('auto_send_invoices', 'Automatisch mailen',
                                doesAutoSend, 'account')}
                        </div>
                        <div className='right'>
                            {renderToggleField('use_bol_text', 'Gebruik Bol tekst', settings.useBolText, 'settings')}
                            {renderToggleField('use_platform_text', 'Gebruik Bolmate tekst', settings.usePlatformText,
                                'settings')}
                            {renderToggleField('participate_kor', 'Deelnemer KOR', settings.participateKOR, 'settings')}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function renderField(key: string, label: string, value: any, entity: 'account' | 'settings', error?: ErrorType,
                         errorKey?: keyof AccountAdminErrors) {
        if (mode === DataMode.DETAILS) return (
            <div className='detail-field'>
                <span className='key'>{label}</span>
                <span className='value'>{value}</span>
            </div>
        );
        else {
            const onFocus = errorKey ? onResetError(errorKey) : undefined;
            return (
                <BolmateInput value={value} onChange={onChange(key, entity)} label={label} error={error}
                              onFocus={onFocus} disabled={loading} className='edit-field'/>
            );
        }
    }

    function renderToggleField(key: string, label: string, value: boolean, entity: 'account' | 'settings') {
        if (mode === DataMode.DETAILS)
            return renderField(key, label, value ? 'Aan' : 'Uit', entity)
        return (
            <div className='toggle-field'>
                <label className='toggle-label'>{label}</label>
                <BolmateSwitch value={value} onChange={onChange(key, entity)} labels={['', value ? 'Aan' : 'Uit']}
                               disabled={loading}/>
            </div>
        );
    }

    function renderCountryField(country: Country) {
        if (mode === DataMode.DETAILS)
            return renderField('country', 'Land', country, 'account');
        return <CountryField onChange={onChange('country', 'account')} value={country} className='edit-field'
                             labelAsValue/>;
    }

    function renderFooter() {
        return (
            <div className='footer-container'>
                <BolmateButton onClick={onClose} label='Sluiten' disabled={loading}/>
                {userIsAdmin() ? <BolmateButton onClick={onSubmit} label='Opslaan' disabled={loading}/> : <div/>}
            </div>
        );
    }

    return (
        <div className='account-details'>
            {renderHeader()}
            {renderBody()}
            {renderFooter()}
        </div>
    )
}